"use client"

import React, { useState, useEffect, useRef, useContext } from "react"
import ApexCharts from "apexcharts"
import { styled, useTheme, ThemeProvider, createTheme } from "@mui/material/styles"
import {
  Grid,
  Paper,
  Typography,
  Button,
  CssBaseline,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Tooltip as MuiTooltip,
  Collapse,
  IconButton,
  Chip,
  Divider,
  Card,
  CardContent,
} from "@mui/material"
import { useTranslation } from "../common/components/LocalizationProvider"
import { formatDate } from "../common/util/formatter"
import {
  LightMode,
  DarkMode,
  Info,
  FilterList,
  ExpandMore,
  ExpandLess,
  Dashboard as DashboardIcon,
  TrendingUp,
  Speed,
  Warning,
  CheckCircle,
  Timeline,
  BarChart,
} from "@mui/icons-material"

// Interface global para resolver o erro do ApexCharts
declare global {
  interface Window {
    ApexCharts?: {
      colors: string[]
    }
  }
}

// Definir interface para o objeto stateCounts para resolver erros de tipagem
interface StateCountsType {
  [key: string]: string[]
}

// Contexto para o tema
const ColorModeContext = React.createContext({
  toggleColorMode: () => {},
})

// Styled components - Versão minimalista
const Root = styled("div")(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#000000", // Fundo preto sempre
  color: "#ffffff",
  transition: "background-color 0.2s ease",
}))

const Header = styled("header")(({ theme }) => ({
  padding: theme.spacing(2, 3),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#ffffff",
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#333" : "#e0e0e0"}`,
  boxShadow: "none",
}))

const Content = styled("div")(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(3),
  maxWidth: "1600px", // Largura máxima ainda maior
  margin: "0 auto", // Centralizar
  overflow: "auto",
  width: "100%",
}))

const StatsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  flexWrap: "wrap",
  justifyContent: "center",
}))

const StatCard = styled(Card)(({ theme }) => ({
  minWidth: 180,
  backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#ffffff",
  border: `1px solid ${theme.palette.mode === "dark" ? "#333" : "#e0e0e0"}`,
  borderRadius: 8,
  boxShadow: "none",
  transition: "none",
  "&:hover": {
    boxShadow: theme.palette.mode === "dark" ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.1)",
  },
}))

const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 8,
  backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#ffffff",
  border: `1px solid ${theme.palette.mode === "dark" ? "#333" : "#e0e0e0"}`,
  boxShadow: "none",
  transition: "none",
  position: "relative",
  overflow: "hidden",
  margin: "0 auto", // Centralizar containers
}))

const ChartWrapper = styled("div")(() => ({
  width: "100%",
  height: "350px", // REDUZIDO de 500px para 350px
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",
}))

// NOVA SOLUÇÃO: Container com altura fixa e scroll interno
const PerformanceChartContainer = styled("div")(() => ({
  width: "100%",
  height: "650px", // Aumentado de 500px para 650px
  maxHeight: "650px",
  overflow: "auto", // Scroll quando necessário
  position: "relative",
  // Estilização do scroll
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#1a1a1a",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#333",
    borderRadius: "4px",
    "&:hover": {
      background: "#444",
    },
  },
}))

// NOVA SOLUÇÃO: Container para timeline com altura fixa e scroll interno
const TimelineChartScrollContainer = styled("div")(() => ({
  width: "100%",
  height: "700px", // Aumentado de 500px para 700px
  maxHeight: "700px",
  overflow: "auto", // Scroll quando necessário
  position: "relative",
  // Estilização do scroll
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#1a1a1a",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#333",
    borderRadius: "4px",
    "&:hover": {
      background: "#444",
    },
  },
}))

// MELHORADO: Wrapper para gráficos de performance com altura otimizada
const PerformanceChartWrapper = styled("div")<{ itemCount?: number }>(({ itemCount = 10 }) => {
  return {
    width: "100%",
    height: "630px", // Aumentado de 480px para 630px
    minHeight: "630px",
    maxHeight: "630px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  }
})

const ActivityChartWrapper = styled("div")<{ itemCount?: number }>(({ itemCount = 10 }) => ({
  width: "100%",
  height: "680px", // Aumentado de 380px para 480px
  minHeight: "680px",
  maxHeight: "680px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",
}))

const ChartTitle = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center", // Centralizar títulos
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1),
}))

const ChartTitleText = styled(Typography)(({ theme }) => ({
  fontSize: 18, // Aumentado de 16 para 18
  fontWeight: 600,
  color: theme.palette.text.primary,
}))

const ChartIcon = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  borderRadius: 6,
  backgroundColor: theme.palette.mode === "dark" ? "#333" : "#f5f5f5",
  color: theme.palette.text.secondary,
  "& svg": {
    fontSize: 18,
  },
}))

const FilterContainer = styled(Collapse)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}))

const FilterContent = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#ffffff",
  border: `2px solid ${theme.palette.mode === "dark" ? "#333" : "#e0e0e0"}`,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  position: "sticky",
  top: 0,
  zIndex: 100,
}))

const FilterGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: theme.spacing(2),
  alignItems: "end",
}))

const LoaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  backgroundColor: theme.palette.mode === "dark" ? "#121212" : "#fafafa",
  gap: theme.spacing(2),
}))

const LoaderAnimation = styled("div")(({ theme }) => ({
  position: "relative",
  width: 40,
  height: 40,
  "& div": {
    position: "absolute",
    width: 32,
    height: 32,
    margin: 4,
    border: `3px solid ${theme.palette.text.secondary}`,
    borderRadius: "50%",
    animation: "loader-animation 1.2s linear infinite",
    borderColor: `${theme.palette.text.secondary} transparent transparent transparent`,
  },
  "@keyframes loader-animation": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },
}))

const ErrorContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  height: "100vh",
  backgroundColor: theme.palette.mode === "dark" ? "#121212" : "#fafafa",
  gap: theme.spacing(2),
}))

const SectionDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(4, 0),
  "&::before, &::after": {
    borderColor: theme.palette.mode === "dark" ? "#333" : "#e0e0e0",
  },
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: 24, // Aumentado de 20 para 24
  fontWeight: 600,
  textAlign: "center", // Centralizado
  marginBottom: theme.spacing(3),
  color: theme.palette.text.primary,
}))

const TimelineChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2), // REDUZIDO de 3 para 2
  borderRadius: 8,
  backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#ffffff",
  border: `1px solid ${theme.palette.mode === "dark" ? "#333" : "#e0e0e0"}`,
  boxShadow: "none",
  transition: "none",
  position: "relative",
  overflow: "hidden",
  margin: "0 auto",
}))

// NOVO: Indicador de dados
const DataIndicator = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  padding: theme.spacing(0.5, 1),
  backgroundColor: "rgba(255, 255, 255, 0.05)",
  borderRadius: 4,
  fontSize: "12px",
  color: theme.palette.text.secondary,
}))

// Componente principal do Dashboard
const Dashboard = () => {
  const theme = useTheme()
  const colorMode = useContext(ColorModeContext)
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const t = useTranslation()
  const isDark = theme.palette.mode === "dark"

  // Estados para armazenar dados
  const [devices, setDevices] = useState([])
  const [positions, setPositions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Estados para filtros e visualização
  const [deviceFilter, setDeviceFilter] = useState("all")
  const [dataLimit, setDataLimit] = useState("10")
  const [filtersApplied, setFiltersApplied] = useState(false)
  const [showFilters, setShowFilters] = useState(true) // MUDANÇA: Filtros visíveis por padrão

  // Refs para os gráficos
  const alarmChartRef = useRef(null)
  const ignitionChartRef = useRef(null)
  const statesChartRef = useRef(null)
  const odometerChartRef = useRef(null)
  const hoursChartRef = useRef(null)
  const todayChartRef = useRef(null)

  // Cores neutras para os gráficos
  const textColor = theme.palette.text.primary

  // MELHORIA: Paleta de cores expandida para garantir cores únicas
  const deviceColors = [
    "#10b981", // Verde esmeralda
    "#3b82f6", // Azul
    "#f59e0b", // Âmbar
    "#ef4444", // Vermelho
    "#8b5cf6", // Violeta
    "#f97316", // Laranja
    "#06b6d4", // Ciano
    "#84cc16", // Lima
    "#ec4899", // Rosa
    "#6366f1", // Índigo
    "#14b8a6", // Teal
    "#f472b6", // Pink
    "#a855f7", // Roxo
    "#22c55e", // Verde
    "#0ea5e9", // Azul céu
    "#fb7185", // Rosa claro
    "#dc2626", // Vermelho escuro
    "#7c3aed", // Violeta escuro
    "#059669", // Verde escuro
    "#0284c7", // Azul escuro
  ]

  // Configuração global para cores neutras do ApexCharts
  useEffect(() => {
    if (window.ApexCharts) {
      window.ApexCharts.colors = deviceColors
    }
  }, [theme])

  // Função para buscar dados de dispositivos e posições
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        // Buscar dispositivos da API real
        const devicesResponse = await fetch("/api/devices")
        if (!devicesResponse.ok) {
          throw new Error(t("errorFetchingDevices") || "Erro ao buscar dispositivos")
        }
        const devicesData = await devicesResponse.json()

        // Buscar posições da API real
        const positionsResponse = await fetch("/api/positions")
        if (!positionsResponse.ok) {
          throw new Error(t("errorFetchingPositions") || "Erro ao buscar posições")
        }
        const positionsData = await positionsResponse.json()

        setDevices(devicesData)
        setPositions(positionsData)
        setLoading(false)
      } catch (err) {
        console.error("Erro ao carregar dados:", err)
        setError(t("errorLoadingData") || "Ocorreu um erro ao carregar os dados. Por favor, tente novamente.")
        setLoading(false)
      }
    }

    fetchData()
  }, [t])

  const applyFilters = () => {
    setFiltersApplied(true)
  }

  const filteredPositions = React.useMemo(() => {
    let filtered = positions

    // Aplicar filtro de dispositivo
    if (deviceFilter !== "all") {
      filtered = filtered.filter((position) => {
        return position.deviceId.toString() === deviceFilter
      })
    }

    // Aplicar limite de dados
    if (dataLimit !== "9999") {
      filtered = filtered.slice(0, Number.parseInt(dataLimit.toString()))
    }

    return filtered
  }, [positions, deviceFilter, dataLimit])

  // Estatísticas para os cards
  const stats = React.useMemo(() => {
    const onlineDevices = devices.filter((d) => d.status === "online").length
    const totalDevices = devices.length
    const movingDevices = filteredPositions.filter((p) => p.attributes?.motion).length
    const alarmsCount = filteredPositions.filter((p) => p.attributes?.alarm).length

    return {
      total: totalDevices,
      online: onlineDevices,
      offline: totalDevices - onlineDevices,
      moving: movingDevices,
      stopped: filteredPositions.length - movingDevices,
      alarms: alarmsCount,
    }
  }, [devices, filteredPositions])

  // Configuração base para todos os gráficos - COM TOOLBAR DE EXPORTAÇÃO
  const getBaseChartOptions = () => ({
    chart: {
      foreColor: textColor,
      background: "transparent",
      fontFamily: "'Inter', sans-serif",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
        export: {
          csv: {
            filename: "dashboard-data",
            columnDelimiter: ",",
            headerCategory: "Categoria",
            headerValue: "Valor",
          },
          svg: {
            filename: "dashboard-chart",
          },
          png: {
            filename: "dashboard-chart",
          },
        },
      },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
      },
    },
    theme: {
      mode: isDark ? "dark" : "light",
    },
    tooltip: {
      theme: isDark ? "dark" : "light",
      style: {
        fontSize: "14px",
        fontFamily: "'Inter', sans-serif",
      },
    },
    legend: {
      labels: {
        colors: textColor,
      },
      fontSize: "14px",
      fontWeight: 500,
    },
    title: {
      style: {
        color: textColor,
        fontSize: "16px",
        fontWeight: "600",
      },
    },
    xaxis: {
      labels: {
        style: {
          colors: textColor,
          fontSize: "12px",
          fontWeight: 400,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: textColor,
          fontSize: "12px",
          fontWeight: 400,
        },
      },
    },
    grid: {
      borderColor: isDark ? "#333" : "#e0e0e0",
      strokeDashArray: 1,
    },
    stroke: {
      width: 2,
    },
  })

  // GRÁFICO DE ALARMES
  useEffect(() => {
    if (!alarmChartRef.current || loading || filteredPositions.length === 0 || devices.length === 0) return

    const stateCounts: StateCountsType = {}
    stateCounts["Normal"] = []

    let limitedPositions = filteredPositions
    if (dataLimit !== "9999" && dataLimit !== 9999) {
      limitedPositions = filteredPositions.slice(0, Number.parseInt(dataLimit.toString()))
    }

    limitedPositions.forEach((position) => {
      if (position.attributes && position.attributes.alarm) {
        const alarmType = position.attributes.alarm
        if (!stateCounts[alarmType]) {
          stateCounts[alarmType] = []
        }
        stateCounts[alarmType].push(position.deviceId)
      } else {
        stateCounts["Normal"].push(position.deviceId)
      }
    })

    const stateKeys = Object.keys(stateCounts)
    const stateValues = stateKeys.map((key) => (stateCounts[key] || []).length)

    const options = {
      ...getBaseChartOptions(),
      series: stateValues,
      chart: {
        ...getBaseChartOptions().chart,
        type: "donut",
        height: 300, // REDUZIDO de 450px para 300px
      },
      legend: {
        ...getBaseChartOptions().legend,
        position: "bottom",
        horizontalAlign: "center",
      },
      labels: stateKeys.map((key) => t(`alarm${key}`) || key),
      plotOptions: {
        pie: {
          donut: {
            size: "60%",
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "16px",
                fontWeight: 600,
                color: textColor,
              },
              value: {
                show: true,
                fontSize: "24px",
                fontWeight: 700,
                color: textColor,
              },
              total: {
                show: true,
                label: "Total",
                fontSize: "14px",
                fontWeight: 600,
                color: textColor,
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => Math.round(val) + "%",
        style: {
          fontSize: "13px",
          fontWeight: "600",
          colors: ["#ffffff"],
        },
      },
      colors: ["#10b981", "#ef4444", "#f59e0b", "#3b82f6", "#8b5cf6"],
    }

    const chart = new ApexCharts(alarmChartRef.current, options)
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [devices, filteredPositions, loading, theme, t, textColor, isDark, deviceFilter, dataLimit])

  // GRÁFICO DE IGNIÇÃO
  useEffect(() => {
    if (!ignitionChartRef.current || loading || filteredPositions.length === 0) return

    const stateCounts = { moving: 0, stopped: 0, ralenti: 0 }

    let limitedPositions = filteredPositions
    if (dataLimit !== "9999" && dataLimit !== 9999) {
      limitedPositions = filteredPositions.slice(0, Number.parseInt(dataLimit.toString()))
    }

    limitedPositions.forEach((position) => {
      if (position.attributes && position.attributes.ignition && !position.speed) {
        stateCounts.ralenti++
      } else if (position.attributes && position.attributes.motion) {
        stateCounts.moving++
      } else {
        stateCounts.stopped++
      }
    })

    const options = {
      ...getBaseChartOptions(),
      series: [stateCounts.moving, stateCounts.stopped, stateCounts.ralenti],
      chart: {
        ...getBaseChartOptions().chart,
        type: "pie",
        height: 300, // REDUZIDO de 450px para 300px
      },
      legend: {
        ...getBaseChartOptions().legend,
        position: "bottom",
        horizontalAlign: "center",
      },
      labels: ["Em Movimento", "Parado", "Inativo"],
      dataLabels: {
        enabled: true,
        formatter: (val) => Math.round(val) + "%",
        style: {
          fontSize: "13px",
          fontWeight: "600",
          colors: ["#ffffff"],
        },
      },
      colors: ["#10b981", "#ef4444", "#f59e0b"],
    }

    const chart = new ApexCharts(ignitionChartRef.current, options)
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [filteredPositions, loading, theme, t, textColor, isDark, deviceFilter, dataLimit])

  // GRÁFICO DE ESTADOS AGRUPADOS
  useEffect(() => {
    if (!statesChartRef.current || loading || devices.length === 0) return

    let filteredDevices = devices
    if (deviceFilter !== "all") {
      filteredDevices = devices.filter((device) => device.id.toString() === deviceFilter)
    }

    // Aplicar limite de dados
    if (dataLimit !== "9999") {
      filteredDevices = filteredDevices.slice(0, Number.parseInt(dataLimit.toString()))
    }

    const stateCounts: StateCountsType = {}
    stateCounts["Online"] = []
    stateCounts["12 horas"] = []
    stateCounts["24 horas"] = []
    stateCounts["36 horas"] = []
    stateCounts["48 horas"] = []
    stateCounts["+ 48 horas"] = []

    filteredDevices.forEach((device) => {
      const lastUpdate = new Date().getTime() - new Date(device.lastUpdate).getTime()
      if (device.status === "online") {
        stateCounts["Online"].push(device.name)
      } else if (lastUpdate < 12 * 3600 * 1000) {
        stateCounts["12 horas"].push(device.name)
      } else if (lastUpdate < 24 * 3600 * 1000) {
        stateCounts["24 horas"].push(device.name)
      } else if (lastUpdate < 36 * 3600 * 1000) {
        stateCounts["36 horas"].push(device.name)
      } else if (lastUpdate < 48 * 3600 * 1000) {
        stateCounts["48 horas"].push(device.name)
      } else {
        stateCounts["+ 48 horas"].push(device.name)
      }
    })

    const stateKeys = Object.keys(stateCounts)
    const stateSeries = stateKeys.map((key) => {
      const array = stateCounts[key] || []
      return array.length
    })

    const translatedLabels = ["Online", "12h", "24h", "36h", "48h", "+48h"]

    const options = {
      ...getBaseChartOptions(),
      series: stateSeries,
      chart: {
        ...getBaseChartOptions().chart,
        type: "donut",
        height: 300, // REDUZIDO de 450px para 300px
      },
      legend: {
        ...getBaseChartOptions().legend,
        position: "bottom",
        horizontalAlign: "center",
      },
      labels: translatedLabels,
      plotOptions: {
        pie: {
          donut: {
            size: "60%",
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "16px",
                fontWeight: 600,
                color: textColor,
              },
              value: {
                show: true,
                fontSize: "24px",
                fontWeight: 700,
                color: textColor,
              },
              total: {
                show: true,
                label: "Total",
                fontSize: "14px",
                fontWeight: 600,
                color: textColor,
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => Math.round(val) + "%",
        style: {
          fontSize: "13px",
          fontWeight: "600",
          colors: ["#ffffff"],
        },
      },
      colors: ["#10b981", "#3b82f6", "#f59e0b", "#f97316", "#ef4444", "#8b5cf6"],
    }

    const chart = new ApexCharts(statesChartRef.current, options)
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [devices, loading, theme, t, textColor, isDark, deviceFilter, dataLimit])

  // GRÁFICO DE ODÔMETRO - MELHORADO COM ALTURA FIXA
  useEffect(() => {
    if (!odometerChartRef.current || loading || filteredPositions.length === 0) return

    let displayData = filteredPositions
      .filter((p) => p.attributes?.odometer)
      .map((p) => {
        const device = devices.find((d) => d.id === p.deviceId)
        return {
          deviceName: device?.name || `Device ${p.deviceId}`,
          odometer: p.attributes.odometer,
        }
      })

    // Aplicar filtro de dispositivo
    if (deviceFilter !== "all") {
      const selectedDevice = devices.find((d) => d.id.toString() === deviceFilter)
      if (selectedDevice) {
        displayData = displayData.filter((item) => item.deviceName === selectedDevice.name)
      }
    }

    // Aplicar limite de dados
    if (dataLimit !== "9999") {
      displayData = displayData.slice(0, Number.parseInt(dataLimit.toString()))
    }

    // Ordenar por quilometragem (maior para menor)
    displayData.sort((a, b) => b.odometer - a.odometer)

    const chartData = displayData.map((item) => Math.round(item.odometer / 1000)) // Converter para km
    const categoryLabels = displayData.map((item) => item.deviceName)

    // NOVA SOLUÇÃO: Altura otimizada com limites inteligentes
    const itemCount = displayData.length
    const minHeight = 300
    const maxHeight = 600 // Altura máxima reduzida
    const itemHeight = itemCount > 15 ? 18 : itemCount > 10 ? 22 : 25 // Altura adaptativa
    const dynamicHeight = 610 // Altura fixa

    const options = {
      ...getBaseChartOptions(),
      series: [
        {
          name: "Quilometragem (km)",
          data: chartData,
        },
      ],
      chart: {
        ...getBaseChartOptions().chart,
        type: "bar",
        height: dynamicHeight, // Altura otimizada
      },
      plotOptions: {
        bar: {
          borderRadius: 4, // Reduzido para economizar espaço
          horizontal: true,
          distributed: true,
          barHeight: itemCount > 15 ? "50%" : "60%", // Barras mais finas com muitos dados
          dataLabels: {
            position: "center",
          },
        },
      },
      legend: {
        show: false,
      },
      colors: deviceColors,
      dataLabels: {
        enabled: true,
        style: {
          colors: ["#ffffff"],
          fontWeight: "700",
          fontSize: itemCount > 20 ? "11px" : "13px", // Fonte menor com muitos dados
        },
        formatter: (val) => `${val.toLocaleString()}km`,
      },
      xaxis: {
        categories: categoryLabels,
        title: {
          text: "Quilômetros Percorridos",
          style: {
            fontSize: "14px",
            fontWeight: 600,
            color: textColor,
          },
        },
        labels: {
          formatter: (val) => `${Math.round(val).toLocaleString()}km`,
          style: {
            fontSize: "11px",
            fontWeight: 500,
          },
        },
      },
      yaxis: {
        labels: {
          show: true,
          maxWidth: 150, // Reduzido para economizar espaço
          style: {
            fontSize: itemCount > 20 ? "10px" : "12px", // Fonte adaptativa
            fontWeight: 500,
          },
          formatter: (val) => {
            if (typeof val === "string") {
              return val.length > 12 ? val.substring(0, 12) + "..." : val
            }
            return val
          },
        },
      },
      tooltip: {
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const deviceName = categoryLabels[dataPointIndex]
          const km = series[seriesIndex][dataPointIndex]
          return `
            <div style="padding: 8px 12px; background: #1a1a1a; border: 1px solid #333; border-radius: 6px; max-width: 200px;">
              <div style="color: #fff; font-weight: 600; margin-bottom: 4px; font-size: 13px;">${deviceName}</div>
              <div style="color: #10b981; font-size: 14px; font-weight: 600;">${km.toLocaleString()} km</div>
            </div>
          `
        },
      },
    }

    const chart = new ApexCharts(odometerChartRef.current, options)
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [loading, theme, t, isDark, textColor, deviceFilter, dataLimit, devices, filteredPositions])

  // GRÁFICO DE HORAS - MELHORADO COM ALTURA FIXA
  useEffect(() => {
    if (!hoursChartRef.current || loading || filteredPositions.length === 0) return

    let displayData = filteredPositions
      .filter((p) => p.attributes?.hours)
      .map((p) => {
        const device = devices.find((d) => d.id === p.deviceId)
        return {
          deviceName: device?.name || `Device ${p.deviceId}`,
          hours: Math.round(p.attributes.hours / 3600000), // Converter de ms para horas
        }
      })

    // Aplicar filtro de dispositivo
    if (deviceFilter !== "all") {
      const selectedDevice = devices.find((d) => d.id.toString() === deviceFilter)
      if (selectedDevice) {
        displayData = displayData.filter((item) => item.deviceName === selectedDevice.name)
      }
    }

    // Aplicar limite de dados
    if (dataLimit !== "9999") {
      displayData = displayData.slice(0, Number.parseInt(dataLimit.toString()))
    }

    // Ordenar por horas (maior para menor)
    displayData.sort((a, b) => b.hours - a.hours)

    const chartData = displayData.map((item) => item.hours)
    const categoryLabels = displayData.map((item) => item.deviceName)

    // NOVA SOLUÇÃO: Altura otimizada com limites inteligentes
    const itemCount = displayData.length
    const minHeight = 300
    const maxHeight = 600 // Altura máxima reduzida
    const itemHeight = itemCount > 15 ? 18 : itemCount > 10 ? 22 : 25 // Altura adaptativa
    const dynamicHeight = 610 // Altura fixa

    const options = {
      ...getBaseChartOptions(),
      series: [
        {
          name: "Horas de Operação",
          data: chartData,
        },
      ],
      chart: {
        ...getBaseChartOptions().chart,
        type: "bar",
        height: dynamicHeight, // Altura otimizada
      },
      plotOptions: {
        bar: {
          borderRadius: 4, // Reduzido para economizar espaço
          horizontal: true,
          distributed: true,
          barHeight: itemCount > 15 ? "50%" : "60%", // Barras mais finas com muitos dados
          dataLabels: {
            position: "center",
          },
        },
      },
      legend: {
        show: false,
      },
      colors: deviceColors,
      dataLabels: {
        enabled: true,
        style: {
          colors: ["#ffffff"],
          fontWeight: "700",
          fontSize: itemCount > 20 ? "11px" : "13px", // Fonte menor com muitos dados
        },
        formatter: (val) => `${val.toLocaleString()}h`,
      },
      xaxis: {
        categories: categoryLabels,
        title: {
          text: "Horas de Operação",
          style: {
            fontSize: "14px",
            fontWeight: 600,
            color: textColor,
          },
        },
        labels: {
          formatter: (val) => `${Math.round(val).toLocaleString()}h`,
          style: {
            fontSize: "11px",
            fontWeight: 500,
          },
        },
      },
      yaxis: {
        labels: {
          show: true,
          maxWidth: 150, // Reduzido para economizar espaço
          style: {
            fontSize: itemCount > 20 ? "10px" : "12px", // Fonte adaptativa
            fontWeight: 500,
          },
          formatter: (val) => {
            if (typeof val === "string") {
              return val.length > 12 ? val.substring(0, 12) + "..." : val
            }
            return val
          },
        },
      },
      tooltip: {
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const deviceName = categoryLabels[dataPointIndex]
          const hours = series[seriesIndex][dataPointIndex]
          return `
            <div style="padding: 8px 12px; background: #1a1a1a; border: 1px solid #333; border-radius: 6px; max-width: 200px;">
              <div style="color: #fff; font-weight: 600; margin-bottom: 4px; font-size: 13px;">${deviceName}</div>
              <div style="color: #10b981; font-size: 14px; font-weight: 600;">${hours.toLocaleString()} horas</div>
            </div>
          `
        },
      },
    }

    const chart = new ApexCharts(hoursChartRef.current, options)
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [loading, theme, t, isDark, textColor, deviceFilter, dataLimit, devices, filteredPositions])

  // GRÁFICO DE ATIVIDADES - MELHORADO
  useEffect(() => {
    if (!todayChartRef.current || loading || devices.length === 0) return

    const now = new Date()
    const from = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
    const to = from + 1000 * 60 * 60 * 24

    let _devices = [...devices].sort((a, b) => a.name.localeCompare(b.name))
    if (deviceFilter !== "all") {
      _devices = _devices.filter((device) => device.id.toString() === deviceFilter)
    }

    if (dataLimit !== "9999" && dataLimit !== 9999) {
      _devices = _devices.slice(0, Number.parseInt(dataLimit.toString()))
    }

    // MELHORIA: Altura fixa ao invés de dinâmica
    const dynamicHeight = 660 // Aumentado de 360px para 460px

    const options = {
      ...getBaseChartOptions(),
      series: [],
      chart: {
        ...getBaseChartOptions().chart,
        type: "rangeBar",
        height: dynamicHeight,
        offsetY: -10, // NOVO: Reduzir margem superior
        offsetX: 0,
      },
      plotOptions: {
        bar: {
          horizontal: true,
          rangeBarGroupRows: true,
          borderRadius: 4, // REDUZIDO de 6 para 4
          barHeight: "60%", // REDUZIDO de 70% para 60%
          dataLabels: {
            hideOverflowingLabels: false,
          },
        },
      },
      xaxis: {
        min: from,
        max: to,
        type: "datetime",
        labels: {
          datetimeUTC: false,
          style: {
            fontSize: "12px",
            fontWeight: 400,
          },
          format: "HH:mm", // MELHORIA: Formato mais limpo
        },
      },
      yaxis: {
        labels: {
          style: {
            fontSize: "12px",
            fontWeight: 400,
          },
          maxWidth: 150, // MELHORIA: Largura máxima
          formatter: (val) => {
            // MELHORIA: Truncar nomes longos
            if (typeof val === "string" && val.length > 12) {
              return val.substring(0, 12) + "..."
            }
            return val
          },
        },
      },
      legend: {
        show: true,
        position: "top",
        fontSize: "14px",
        fontWeight: 500,
        horizontalAlign: "center",
      },
      tooltip: {
        // MELHORIA: Tooltip customizado
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const seriesName = w.config.series[seriesIndex].name
          const deviceName = w.config.series[seriesIndex].data[dataPointIndex].x
          const startTime = new Date(w.config.series[seriesIndex].data[dataPointIndex].y[0])
          const endTime = new Date(w.config.series[seriesIndex].data[dataPointIndex].y[1])
          const duration = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60)) // em minutos

          return `
            <div style="padding: 12px; background: #1a1a1a; border: 1px solid #333; border-radius: 8px; min-width: 200px;">
              <div style="color: #fff; font-weight: 600; margin-bottom: 6px;">${deviceName}</div>
              <div style="color: ${seriesName === "Viagens" ? "#10b981" : "#ef4444"}; font-size: 14px; margin-bottom: 4px;">
                ${seriesName}
              </div>
              <div style="color: #aaa; font-size: 12px; margin-bottom: 2px;">
                Início: ${formatDate(startTime.toISOString())}
              </div>
              <div style="color: #aaa; font-size: 12px; margin-bottom: 2px;">
                Fim: ${formatDate(endTime.toISOString())}
              </div>
              <div style="color: #fff; font-size: 12px; font-weight: 500;">
                Duração: ${duration} min
              </div>
            </div>
          `
        },
      },
      dataLabels: {
        enabled: false, // MELHORIA: Desabilitar para evitar poluição visual
      },
      grid: {
        ...getBaseChartOptions().grid,
        padding: {
          top: 0, // NOVO: Remover padding superior
          bottom: 0, // NOVO: Remover padding inferior
          left: 10,
          right: 10,
        },
      },
    }

    const chart = new ApexCharts(todayChartRef.current, options)
    chart.render()

    const fetchTripsAndStops = async () => {
      try {
        const deviceIds = _devices.map((d) => `deviceId=${d.id}`).join("&")
        const dateParam = `&from=${new Date(from).toISOString()}&to=${new Date(to).toISOString()}`

        const [tripsResponse, stopsResponse] = await Promise.all([
          fetch(`/api/reports/trips?${deviceIds}${dateParam}`, {
            headers: { Accept: "application/json" },
          }),
          fetch(`/api/reports/stops?${deviceIds}${dateParam}`, {
            headers: { Accept: "application/json" },
          }),
        ])

        if (tripsResponse.ok && stopsResponse.ok) {
          const trips = await tripsResponse.json()
          const stops = await stopsResponse.json()

          const stopsSeries = {
            color: "#ef4444",
            name: "Paradas",
            data: stops.map((s) => {
              const deviceName = _devices.find((d) => d.id === s.deviceId)?.name || ""
              return {
                x: deviceName,
                y: [new Date(s.startTime).getTime(), new Date(s.endTime).getTime()],
              }
            }),
          }

          const tripsSeries = {
            color: "#10b981",
            name: "Viagens",
            data: trips.map((s) => {
              const deviceName = _devices.find((d) => d.id === s.deviceId)?.name || ""
              return {
                x: deviceName,
                y: [new Date(s.startTime).getTime(), new Date(s.endTime).getTime()],
              }
            }),
          }

          chart.updateOptions({
            series: [stopsSeries, tripsSeries],
          })
        }
      } catch (error) {
        console.error("Erro ao buscar dados:", error)
      }
    }

    fetchTripsAndStops()

    return () => {
      chart.destroy()
    }
  }, [devices, loading, theme, t, deviceFilter, dataLimit])

  // Manipuladores de eventos
  const handleDeviceFilterChange = (event) => {
    setDeviceFilter(event.target.value)
    setFiltersApplied(true)
  }

  const handleDataLimitChange = (event) => {
    setDataLimit(event.target.value)
    setFiltersApplied(true)
  }

  // Calcular número de itens para altura dinâmica
  const currentItemCount = React.useMemo(() => {
    let count = filteredPositions.length
    if (dataLimit !== "9999") {
      count = Math.min(count, Number.parseInt(dataLimit.toString()))
    }
    return count
  }, [filteredPositions, dataLimit])

  // Exibir indicador de carregamento
  if (loading) {
    return (
      <LoaderContainer>
        <LoaderAnimation>
          <div></div>
        </LoaderAnimation>
        <Typography variant="body1" color="text.secondary">
          Carregando Dashboard...
        </Typography>
      </LoaderContainer>
    )
  }

  // Exibir mensagem de erro
  if (error) {
    return (
      <ErrorContainer>
        <Typography variant="h5" color="error" fontWeight={600} gutterBottom>
          Erro no Sistema
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={400}>
          {error}
        </Typography>
        <Button
          variant="contained"
          size="medium"
          onClick={() => window.location.reload()}
          sx={{ mt: 2, borderRadius: 2, px: 3 }}
        >
          Tentar Novamente
        </Button>
      </ErrorContainer>
    )
  }

  // Renderizar dashboard
  return (
    <Root>
      <Header>
        <Box display="flex" alignItems="center" gap={2}>
          <ChartIcon>
            <DashboardIcon />
          </ChartIcon>
          <Box>
            <Typography variant="h5" component="h1" fontWeight={600}>
              Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitoramento em Tempo Real
            </Typography>
          </Box>
        </Box>

        <Box display="flex" gap={1} alignItems="center">
          <IconButton color="inherit" onClick={() => setShowFilters(!showFilters)} size="small">
            {showFilters ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          <Button
            variant="outlined"
            onClick={colorMode.toggleColorMode}
            startIcon={isDark ? <LightMode /> : <DarkMode />}
            size="small"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              px: 2,
            }}
          >
            {isDark ? "Claro" : "Escuro"}
          </Button>
        </Box>
      </Header>

      <Content>
        {/* Filtros */}
        <FilterContainer in={showFilters}>
          <FilterContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <FilterList sx={{ color: "text.secondary" }} />
              <Typography variant="h6" fontWeight={600} sx={{ color: "text.primary" }}>
                Filtros e Configurações
              </Typography>
              <Chip
                label="FILTROS"
                size="small"
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  color: "text.secondary",
                  fontWeight: 600,
                  fontSize: "10px",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                }}
              />
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <FormControl variant="outlined" fullWidth size="medium">
                  <InputLabel sx={{ color: "text.secondary" }}>Dispositivo</InputLabel>
                  <Select
                    value={deviceFilter}
                    onChange={handleDeviceFilterChange}
                    label="Dispositivo"
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#333",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#10b981",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#10b981",
                      },
                    }}
                  >
                    <MenuItem value="all">
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CheckCircle sx={{ fontSize: 16, color: "text.secondary" }} />
                        Todos os dispositivos
                      </Box>
                    </MenuItem>
                    {devices.map((device) => (
                      <MenuItem key={device.id} value={device.id.toString()}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <div
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: device.status === "online" ? "#666" : "#444",
                            }}
                          />
                          {device.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl variant="outlined" fullWidth size="medium">
                  <InputLabel sx={{ color: "text.secondary" }}>Limite de Dados</InputLabel>
                  <Select
                    value={dataLimit}
                    onChange={handleDataLimitChange}
                    label="Limite de Dados"
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#333",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#3b82f6",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#3b82f6",
                      },
                    }}
                  >
                    <MenuItem value={5}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <BarChart sx={{ fontSize: 16, color: "text.secondary" }} />5 itens
                      </Box>
                    </MenuItem>
                    <MenuItem value={10}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <BarChart sx={{ fontSize: 16, color: "text.secondary" }} />
                        10 itens
                      </Box>
                    </MenuItem>
                    <MenuItem value={20}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <BarChart sx={{ fontSize: 16, color: "text.secondary" }} />
                        20 itens
                      </Box>
                    </MenuItem>
                    <MenuItem value={50}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <BarChart sx={{ fontSize: 16, color: "text.secondary" }} />
                        50 itens
                      </Box>
                    </MenuItem>
                    <MenuItem value={"9999"}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <TrendingUp sx={{ fontSize: 16, color: "text.secondary" }} />
                        Todos
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  startIcon={<FilterList />}
                  fullWidth
                  size="large"
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "16px",
                    py: 1.5,
                    bgcolor: "#333",
                    color: "white",
                    "&:hover": {
                      bgcolor: "#444",
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  Aplicar Filtros
                </Button>
              </Grid>
            </Grid>

            {filtersApplied && (
              <Box sx={{ mt: 2, p: 2, bgcolor: "#1a1a1a", borderRadius: 2, border: "1px solid #333" }}>
                <Typography variant="body2" color="text.secondary">
                  <CheckCircle sx={{ fontSize: 16, color: "text.secondary", mr: 1, verticalAlign: "middle" }} />
                  Filtros aplicados:{" "}
                  {deviceFilter === "all"
                    ? "Todos os dispositivos"
                    : devices.find((d) => d.id.toString() === deviceFilter)?.name}{" "}
                  | Limite: {dataLimit === "9999" ? "Todos os dispositivos" : `${dataLimit} itens`}
                </Typography>
              </Box>
            )}
          </FilterContent>
        </FilterContainer>

        {/* Cards de estatísticas */}
        <StatsContainer>
          <StatCard>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <CheckCircle sx={{ fontSize: 32, color: "#10b981", mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>
                {stats.online}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Online
              </Typography>
            </CardContent>
          </StatCard>

          <StatCard>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <Speed sx={{ fontSize: 32, color: "#3b82f6", mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>
                {stats.moving}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Em Movimento
              </Typography>
            </CardContent>
          </StatCard>

          <StatCard>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <Warning sx={{ fontSize: 32, color: "#f59e0b", mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>
                {stats.alarms}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Alarmes
              </Typography>
            </CardContent>
          </StatCard>

          <StatCard>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <TrendingUp sx={{ fontSize: 32, color: "#8b5cf6", mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total
              </Typography>
            </CardContent>
          </StatCard>
        </StatsContainer>

        {/* Seção de Status */}
        <SectionTitle>Status dos Dispositivos</SectionTitle>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
          <Grid container spacing={4} sx={{ maxWidth: "1400px" }}>
            <Grid item xs={12} lg={4}>
              <ChartContainer>
                <ChartTitle>
                  <ChartIcon>
                    <CheckCircle />
                  </ChartIcon>
                  <ChartTitleText>Status Online</ChartTitleText>
                  <MuiTooltip title="Status de conectividade dos dispositivos">
                    <Info fontSize="small" />
                  </MuiTooltip>
                </ChartTitle>
                <ChartWrapper ref={statesChartRef} />
              </ChartContainer>
            </Grid>

            <Grid item xs={12} lg={4}>
              <ChartContainer>
                <ChartTitle>
                  <ChartIcon>
                    <Speed />
                  </ChartIcon>
                  <ChartTitleText>Movimento</ChartTitleText>
                  <MuiTooltip title="Estado de movimento dos dispositivos">
                    <Info fontSize="small" />
                  </MuiTooltip>
                </ChartTitle>
                <ChartWrapper ref={ignitionChartRef} />
              </ChartContainer>
            </Grid>

            <Grid item xs={12} lg={4}>
              <ChartContainer>
                <ChartTitle>
                  <ChartIcon>
                    <Warning />
                  </ChartIcon>
                  <ChartTitleText>Alarmes</ChartTitleText>
                  <MuiTooltip title="Distribuição de alarmes ativos">
                    <Info fontSize="small" />
                  </MuiTooltip>
                </ChartTitle>
                <ChartWrapper ref={alarmChartRef} />
              </ChartContainer>
            </Grid>
          </Grid>
        </Box>

        <SectionDivider>
          <Chip label="RELATÓRIOS DE PERFORMANCE" size="small" />
        </SectionDivider>

        {/* Seção de Performance - MELHORADA */}
        <SectionTitle>Relatórios de Performance</SectionTitle>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
          <Grid container spacing={4} sx={{ maxWidth: "1800px" }}>
            <Grid item xs={12} lg={8}>
              <ChartContainer>
                <ChartTitle>
                  <ChartIcon>
                    <BarChart />
                  </ChartIcon>
                  <ChartTitleText>Quilometragem</ChartTitleText>
                  <MuiTooltip title="Distância total percorrida por dispositivo - Altura otimizada para melhor visualização">
                    <Info fontSize="small" />
                  </MuiTooltip>
                </ChartTitle>

                {/* NOVO: Indicador de dados */}
                <DataIndicator>
                  <BarChart sx={{ fontSize: 14 }} />
                  <Typography variant="caption">{currentItemCount} dispositivos • Altura otimizada</Typography>
                </DataIndicator>

                {/* NOVA SOLUÇÃO: Container com scroll */}
                <PerformanceChartContainer>
                  <PerformanceChartWrapper itemCount={currentItemCount} ref={odometerChartRef} />
                </PerformanceChartContainer>
              </ChartContainer>
            </Grid>

            <Grid item xs={12} lg={8}>
              <ChartContainer>
                <ChartTitle>
                  <ChartIcon>
                    <TrendingUp />
                  </ChartIcon>
                  <ChartTitleText>Horas de Uso</ChartTitleText>
                  <MuiTooltip title="Tempo total de operação por dispositivo - Altura otimizada para melhor visualização">
                    <Info fontSize="small" />
                  </MuiTooltip>
                </ChartTitle>

                {/* NOVO: Indicador de dados */}
                <DataIndicator>
                  <TrendingUp sx={{ fontSize: 14 }} />
                  <Typography variant="caption">{currentItemCount} dispositivos • Altura otimizada</Typography>
                </DataIndicator>

                {/* NOVA SOLUÇÃO: Container com scroll */}
                <PerformanceChartContainer>
                  <PerformanceChartWrapper itemCount={currentItemCount} ref={hoursChartRef} />
                </PerformanceChartContainer>
              </ChartContainer>
            </Grid>
          </Grid>
        </Box>

        <SectionDivider>
          <Chip label="ATIVIDADES DO DIA" size="small" />
        </SectionDivider>

        {/* Seção de Atividades */}
        <SectionTitle>Timeline de Atividades</SectionTitle>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box sx={{ width: "100%", maxWidth: "1500px" }}>
            <TimelineChartContainer>
              <ChartTitle>
                <ChartIcon>
                  <Timeline />
                </ChartIcon>
                <ChartTitleText>Viagens e Paradas Hoje</ChartTitleText>
                <MuiTooltip title="Timeline das atividades do dia atual - Altura otimizada">
                  <Info fontSize="small" />
                </MuiTooltip>
              </ChartTitle>

              {/* NOVO: Indicador de dados */}
              <DataIndicator>
                <Timeline sx={{ fontSize: 14 }} />
                <Typography variant="caption">
                  {devices.filter((d) => deviceFilter === "all" || d.id.toString() === deviceFilter).length}{" "}
                  dispositivos • Altura otimizada
                </Typography>
              </DataIndicator>

              {/* NOVA SOLUÇÃO: Container com scroll */}
              <TimelineChartScrollContainer>
                <ActivityChartWrapper ref={todayChartRef} />
              </TimelineChartScrollContainer>
            </TimelineChartContainer>
          </Box>
        </Box>
      </Content>
    </Root>
  )
}

// Componente com tema - versão minimalista
const DashboardWithTheme = () => {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)")
  const [mode, setMode] = React.useState<"light" | "dark">(prefersDarkMode ? "dark" : "light")

  React.useEffect(() => {
    setMode(prefersDarkMode ? "dark" : "light")
  }, [prefersDarkMode])

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"))
      },
    }),
    [],
  )

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: "dark", // Sempre modo escuro
          primary: {
            main: "#10b981",
          },
          secondary: {
            main: "#3b82f6",
          },
          background: {
            default: "#000000", // Fundo preto
            paper: "#1a1a1a", // Papel cinza escuro
          },
          text: {
            primary: "#ffffff",
            secondary: "#aaaaaa",
          },
        },
        typography: {
          fontFamily: "'Inter', sans-serif",
          h1: { fontWeight: 600 },
          h2: { fontWeight: 600 },
          h3: { fontWeight: 600 },
          h4: { fontWeight: 600 },
          h5: { fontWeight: 600 },
          h6: { fontWeight: 500 },
        },
        shape: {
          borderRadius: 8,
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontWeight: 500,
                borderRadius: 8,
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 8,
              },
            },
          },
        },
      }),
    [],
  )

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Dashboard />
      </ThemeProvider>
    </ColorModeContext.Provider>
  )
}

export default DashboardWithTheme
